<script setup lang="ts">
type BasicInfoValue = string | number | null | undefined
type BasicInfoValueInput = BasicInfoValue | BasicInfoValue[]
type ModalFieldKey =
  | 'exchangeAbbreviation'
  | 'regulationStatus'
  | 'companyName'
  | 'exchangeCategory'
  | 'tradingType'
  | 'businessOperations'
  | 'registeredArea'
  | 'ecosystemExchange'
  | 'isListed'
  | 'stockCode'
  | 'listedExchange'
  | 'operatingYears'
  | 'companyAddress'
  | 'website'

type ContactType = string

interface InfoItem {
  key?: ModalFieldKey | string
  label: string
  value?: BasicInfoValueInput
  values?: BasicInfoValueInput
  isLink?: boolean
  linkUrl?: string
  linkUrls?: string[]
  img?: string | any
  color?: string
}

interface ContactItem {
  key?: ContactType | string
  label: string
  value?: BasicInfoValueInput
  values?: BasicInfoValueInput
  linkUrl?: string
  linkUrls?: string[]
}

interface FieldMeta {
  key: ModalFieldKey
  labelKey?: string
  fallbackLabel: string
  multiline?: boolean
  useLinkValues?: boolean
}

interface NormalizedField {
  key: ModalFieldKey
  label: string
  values: string[]
  urls: string[]
  isEmpty: boolean
  img?: string | any
  color?: string
  renderKey?: string
}

interface NormalizedContactItem {
  type: string
  label: string
  values: string[]
  urls: string[]
  isEmpty: boolean
}

interface HeaderSocialItem {
  type: string
  label: string
  icon: string
}

interface SummaryItem {
  key: string
  label: string
  value: string
  img?: string | any
  contactType?: string
}

interface NationalRegulationDisplay {
  businessType?: number
  tipText?: string
  countryCode?: string
  show?: boolean
  rankPosition?: string | number
  flag?: string
  rankKeyCode?: string
  rankTotal?: string | number
}

type SummarySlot = { kind: 'field'; key: ModalFieldKey } | { kind: 'contact'; key: ContactType }

const props = withDefaults(
  defineProps<{
    infoItems?: InfoItem[]
    contactItems?: ContactItem[]
    whatsappUrl?: string
    xUrl?: string
    facebookUrl?: string
    instagramUrl?: string
    exchangeName?: string
    exchangeId?: string
    nationalRegulationDisplay?: NationalRegulationDisplay | null
    rankingHref?: string
  }>(),
  {
    infoItems: () => [],
    contactItems: () => [],
    whatsappUrl: '',
    xUrl: '',
    facebookUrl: '',
    instagramUrl: '',
    exchangeName: '',
    exchangeId: '',
    nationalRegulationDisplay: null,
    rankingHref: '',
  },
)
const emit = defineEmits<{
  openWebsite: [url: string]
}>()

const { t } = useAppI18n()
const { isRtl } = useRtl()
const matomo = useMatomo()
const isMoreDialogOpen = ref(false)
const expandedSectionKeys = ref<string[]>([])
const overflowingInlineFieldKeys = ref<string[]>([])
const rankingTipElement = ref<HTMLElement | null>(null)
const isRankingTipMultiline = ref(false)

const emptyText = '--'
const COLLAPSED_VALUE_LIMIT = 3
const INLINE_VALUE_LINE_LIMIT = 3
const inlineValueElements = new Map<string, HTMLElement>()
const dialogTitle = computed(() => t('84023'))
/** 卡片区域标题，仅显示"基础信息"，不加"交易所"前缀 */
const cardTitle = computed(() => t('84023'))
const moreButtonLabel = computed(() => t('82485'))

const fieldMetaMap: Record<ModalFieldKey, FieldMeta> = {
  exchangeAbbreviation: {
    key: 'exchangeAbbreviation',
    labelKey: '82571',
    fallbackLabel: '交易所简称',
  },
  regulationStatus: {
    key: 'regulationStatus',
    labelKey: '82382',
    fallbackLabel: '监管状态',
  },
  companyName: {
    key: 'companyName',
    labelKey: '85056',
    fallbackLabel: '企业名称',
  },
  exchangeCategory: {
    key: 'exchangeCategory',
    labelKey: '85057',
    fallbackLabel: '交易所类型',
  },
  tradingType: {
    key: 'tradingType',
    labelKey: '82581',
    fallbackLabel: '交易类型',
  },
  businessOperations: {
    key: 'businessOperations',
    labelKey: '84340',
    fallbackLabel: '经营业务',
  },
  registeredArea: {
    key: 'registeredArea',
    labelKey: '82569',
    fallbackLabel: '注册地区',
  },
  ecosystemExchange: {
    key: 'ecosystemExchange',
    labelKey: '85074',
    fallbackLabel: '是否生态型交易所',
  },
  isListed: {
    key: 'isListed',
    labelKey: '85058',
    fallbackLabel: '是否上市公司',
  },
  stockCode: {
    key: 'stockCode',
    labelKey: '85059',
    fallbackLabel: '上市股票代码',
  },
  listedExchange: {
    key: 'listedExchange',
    labelKey: '85060',
    fallbackLabel: '上市交易所',
  },
  operatingYears: {
    key: 'operatingYears',
    labelKey: '80342',
    fallbackLabel: '经营年限',
  },
  companyAddress: {
    key: 'companyAddress',
    labelKey: '82578',
    fallbackLabel: '公司地址',
    multiline: true,
  },
  website: {
    key: 'website',
    labelKey: '82577',
    fallbackLabel: '网址',
    multiline: true,
    useLinkValues: true,
  },
}

const getFieldLabel = (meta: FieldMeta, sourceLabel = '') =>
  meta.labelKey ? t(meta.labelKey) : sourceLabel.trim() || meta.fallbackLabel
const fixedFieldOrder: ModalFieldKey[] = ['registeredArea', 'operatingYears', 'exchangeAbbreviation', 'website']
/** 卡片摘要中的多值字段保留全部值，弹窗复用父级传入的排序结果。 */
const SUMMARY_MULTI_VALUE_FIELDS = new Set<ModalFieldKey>(['exchangeCategory', 'tradingType', 'businessOperations'])
const dialogTopFieldOrder: ModalFieldKey[] = [
  'exchangeAbbreviation',
  'regulationStatus',
  'registeredArea',
  'operatingYears',
  'companyName',
  'exchangeCategory',
  'tradingType',
  'businessOperations',
  'isListed',
  'stockCode',
  'listedExchange',
  'ecosystemExchange',
  'companyAddress',
]
const dialogBottomFieldOrder: ModalFieldKey[] = ['website']
const substituteFieldOrder: ModalFieldKey[] = [
  'companyName',
  'exchangeCategory',
  'tradingType',
  'businessOperations',
  'ecosystemExchange',
  'isListed',
  'stockCode',
  'listedExchange',
  'companyAddress',
]
const contactTypeOrder: ContactType[] = [
  'phone',
  'email',
  'x',
  'telegram',
  'facebook',
  'instagram',
  'medium',
  'linkedin',
  'youtube',
  'reddit',
  'discord',
  'whatsapp',
  'line',
  'qq',
  'wechat',
  'blog',
  'tumblr',
  'skype',
  'steemit',
  'weibo',
  'other',
]

const contactAliases: Record<string, ContactType> = {
  phone: 'phone',
  telephone: 'phone',
  电话: 'phone',
  联系电话: 'phone',
  email: 'email',
  邮箱: 'email',
  客服邮箱: 'email',
  x: 'x',
  twitter: 'x',
  twitterx: 'x',
  telegram: 'telegram',
  facebook: 'facebook',
  instagram: 'instagram',
  medium: 'medium',
  linkedin: 'linkedin',
  youtube: 'youtube',
  reddit: 'reddit',
  discord: 'discord',
  whatsapp: 'whatsapp',
  line: 'line',
  qq: 'qq',
  wechat: 'wechat',
  weixin: 'wechat',
  blog: 'blog',
  tumblr: 'tumblr',
  skype: 'skype',
  steemit: 'steemit',
  微博: 'weibo',
  weibo: 'weibo',
}

const socialIconBasePath = '/img/exchange-detail/social/'

const normalizeLookupKey = (value: string) =>
  value
    .toLowerCase()
    .replace(/[()\s/_-]+/g, '')
    .replace(/[：:]/g, '')
    .trim()

const sourceInfoItems = computed(() => props.infoItems)
const sourceContactItems = computed(() => props.contactItems)

const isUrl = (value: string) => /^https?:\/\//i.test(value)
const nonLinkContactTypes = new Set(['phone', 'telephone', 'email', 'qq', 'wechat'])
/** 仅使用接口返回的完整链接，电话、邮箱、QQ 和 WeChat 始终保持不可点击。 */
const normalizeContactUrl = (type: string, value: string) => {
  const normalizedValue = value.trim()

  return !nonLinkContactTypes.has(type) && isUrl(normalizedValue) ? normalizedValue : ''
}
const normalizeSingleValue = (value: BasicInfoValue) =>
  value === null || value === undefined ? '' : String(value).trim()
const isMeaningfulValue = (value: string) => Boolean(value && value !== '-' && value !== emptyText)
const normalizeValueList = (value: BasicInfoValueInput, splitLines = false) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeSingleValue(item)).filter(isMeaningfulValue)
  }

  const normalizedValue = normalizeSingleValue(value)

  if (!isMeaningfulValue(normalizedValue)) {
    return []
  }

  if (splitLines && normalizedValue.includes('\n')) {
    return normalizedValue
      .split('\n')
      .map((item) => item.trim())
      .filter(isMeaningfulValue)
  }

  return [normalizedValue]
}

const getFieldKeyFromItem = (item: InfoItem) => {
  if (item.key && fieldMetaMap[item.key as ModalFieldKey]) {
    return item.key as ModalFieldKey
  }

  return null
}

const getContactType = (item: ContactItem | InfoItem) => {
  const candidates = [item.key ? String(item.key) : '', item.label]

  for (const candidate of candidates) {
    const normalizedCandidate = normalizeLookupKey(candidate)

    if (contactAliases[normalizedCandidate]) {
      return contactAliases[normalizedCandidate]
    }
  }

  const fallbackType = candidates.map(normalizeLookupKey).find(Boolean)

  return fallbackType || 'other'
}

const getContactLabel = (type: string, fallbackLabel: string) => {
  if (fallbackLabel.trim()) {
    return fallbackLabel.trim()
  }

  if (type === 'wechat') {
    return 'WeChat'
  }

  if (type === 'weibo') {
    return '微博'
  }

  if (type === 'linkedin') {
    return 'LinkedIn'
  }

  return type === 'x' ? 'X' : type.charAt(0).toUpperCase() + type.slice(1)
}

const normalizedFields = computed<Record<ModalFieldKey, NormalizedField>>(() => {
  const fieldMap = Object.values(fieldMetaMap).reduce<Record<ModalFieldKey, NormalizedField>>(
    (result, meta) => {
      result[meta.key] = {
        key: meta.key,
        label: getFieldLabel(meta),
        values: [],
        urls: [],
        isEmpty: true,
      }

      return result
    },
    {} as Record<ModalFieldKey, NormalizedField>,
  )

  sourceInfoItems.value.forEach((item) => {
    const key = getFieldKeyFromItem(item)

    if (!key) {
      return
    }

    const meta = fieldMetaMap[key]
    const rawValues = item.values ?? item.value
    const normalizedValues = normalizeValueList(rawValues, Boolean(meta.multiline))
    const fallbackUrls = item.linkUrls
      ? item.linkUrls.filter(isUrl)
      : item.linkUrl && isUrl(item.linkUrl)
        ? [item.linkUrl]
        : []
    const urls =
      fallbackUrls.length > 0
        ? fallbackUrls
        : meta.useLinkValues
          ? normalizedValues.filter((value) => isUrl(value))
          : []

    fieldMap[key] = {
      key,
      label: getFieldLabel(meta, item.label),
      values: normalizedValues,
      urls,
      isEmpty: normalizedValues.length === 0,
      img: item.img,
      color: item.color,
      renderKey: `${key}:${normalizedValues.join('|')}`,
    }
  })

  return fieldMap
})

const normalizedContactItems = computed<NormalizedContactItem[]>(() => {
  const contactMap = new Map<string, NormalizedContactItem>()
  const mergeContactItem = (item: ContactItem | InfoItem) => {
    const type = getContactType(item)
    const values = normalizeValueList(item.values ?? item.value, true)
    const fallbackUrls = item.linkUrls ? item.linkUrls.filter(Boolean) : item.linkUrl ? [item.linkUrl] : []
    const urls = values.map((value, index) => normalizeContactUrl(type, fallbackUrls[index] || value))
    const existingItem = contactMap.get(type)

    if (existingItem) {
      values.forEach((value, index) => {
        if (!existingItem.values.includes(value)) {
          existingItem.values.push(value)
          existingItem.urls.push(urls[index] || '')
        }
      })
      existingItem.isEmpty = existingItem.values.length === 0
      return
    }

    contactMap.set(type, {
      type,
      label: getContactLabel(type, item.label),
      values,
      urls,
      isEmpty: values.every((value) => value === emptyText),
    })
  }
  const rawContacts = sourceContactItems.value

  rawContacts.forEach((item) => {
    mergeContactItem(item)
  })

  sourceInfoItems.value.forEach((item) => {
    if (getFieldKeyFromItem(item)) {
      return
    }

    mergeContactItem(item)
  })

  if (props.whatsappUrl && !contactMap.has('whatsapp')) {
    contactMap.set('whatsapp', {
      type: 'whatsapp',
      label: 'WhatsApp',
      values: [props.whatsappUrl],
      urls: [props.whatsappUrl],
      isEmpty: false,
    })
  }

  if (props.xUrl && !contactMap.has('x')) {
    contactMap.set('x', {
      type: 'x',
      label: 'X',
      values: [props.xUrl],
      urls: [props.xUrl],
      isEmpty: false,
    })
  }

  if (props.facebookUrl && !contactMap.has('facebook')) {
    contactMap.set('facebook', {
      type: 'facebook',
      label: 'Facebook',
      values: [props.facebookUrl],
      urls: [props.facebookUrl],
      isEmpty: false,
    })
  }

  if (props.instagramUrl && !contactMap.has('instagram')) {
    contactMap.set('instagram', {
      type: 'instagram',
      label: 'Instagram',
      values: [props.instagramUrl],
      urls: [props.instagramUrl],
      isEmpty: false,
    })
  }

  return Array.from(contactMap.values()).toSorted((first, second) => {
    const firstIndex = contactTypeOrder.indexOf(first.type)
    const secondIndex = contactTypeOrder.indexOf(second.type)
    const normalizedFirstIndex = firstIndex === -1 ? Number.MAX_SAFE_INTEGER : firstIndex
    const normalizedSecondIndex = secondIndex === -1 ? Number.MAX_SAFE_INTEGER : secondIndex

    return normalizedFirstIndex - normalizedSecondIndex
  })
})

const dialogFields = computed(() =>
  dialogTopFieldOrder.map((key) => normalizedFields.value[key]).filter((field) => !field.isEmpty),
)
const dialogBottomFields = computed(() =>
  dialogBottomFieldOrder.map((key) => normalizedFields.value[key]).filter((field) => !field.isEmpty),
)

const topFieldRows = computed(() =>
  Array.from({ length: Math.ceil(dialogFields.value.length / 2) }, (_, index) =>
    dialogFields.value.slice(index * 2, index * 2 + 2),
  ),
)

const contactSections = computed(() => {
  const nonEmptyContacts = normalizedContactItems.value.filter((item) => item.values.length > 0)

  return nonEmptyContacts
})

/** 生成卡片摘要值；多值类型拼接后交给摘要文本的省略号样式处理。 */
const getSummaryValue = (field: NormalizedField) => {
  if (field.isEmpty) {
    return emptyText
  }

  const values = SUMMARY_MULTI_VALUE_FIELDS.has(field.key) ? field.values : field.values.slice(0, 1)

  return values.join('、') || emptyText
}
const toSummaryField = (field: NormalizedField): SummaryItem => ({
  key: field.key,
  label: field.label,
  value: getSummaryValue(field),
  img: field.key === 'registeredArea' ? field.img : undefined,
})
const getSummaryContactItem = (type: ContactType): SummaryItem => {
  const contact = normalizedContactItems.value.find((item) => item.type === type)
  const fallbackLabel = type === 'x' ? 'Twitter' : getContactLabel(type, '')

  return {
    key: `contact-${type}`,
    label: contact?.label || fallbackLabel,
    value: contact?.values[0] || emptyText,
    contactType: type,
  }
}
const getSummaryItem = (slot: SummarySlot) =>
  slot.kind === 'field' ? toSummaryField(normalizedFields.value[slot.key]) : getSummaryContactItem(slot.key)
const summaryCandidates = computed(() => {
  /** 固定字段始终占据卡片摘要位置，空值统一由 getSummaryValue 显示为 `--`。 */
  const fixedSlots: SummarySlot[] = fixedFieldOrder.map((key) => ({ kind: 'field', key }))
  const substituteFieldSlots: SummarySlot[] = substituteFieldOrder
    .filter((key) => !normalizedFields.value[key].isEmpty)
    .map((key) => ({ kind: 'field', key }))
  const contactSlots: SummarySlot[] = contactSections.value.map((contact) => ({ kind: 'contact', key: contact.type }))

  return [...fixedSlots, ...substituteFieldSlots, ...contactSlots].map(getSummaryItem)
})
const hasRankingInfo = computed(() => {
  const rankingInfo = props.nationalRegulationDisplay

  return Boolean(rankingInfo?.show === true && String(rankingInfo.tipText || '').trim())
})
/** 排行榜提示为双行时，摘要仅保留一行字段，避免挤压底部排行提示。 */
const summaryItems = computed(() => {
  const summaryLimit = hasRankingInfo.value ? (isRankingTipMultiline.value ? 2 : 4) : 6

  return summaryCandidates.value.slice(0, summaryLimit)
})
/** 卡片固定两列，字段按阅读顺序交错分配到左右列。 */
const summaryColumns = computed(() => [
  summaryItems.value.filter((_, index) => index % 2 === 0),
  summaryItems.value.filter((_, index) => index % 2 === 1),
])
const hasMoreFields = computed(() => summaryCandidates.value.length > summaryItems.value.length)
const rankingPositionText = computed(() => {
  if (!hasRankingInfo.value || !props.nationalRegulationDisplay) {
    return ''
  }

  return `${props.nationalRegulationDisplay.rankPosition}/${props.nationalRegulationDisplay.rankTotal}`
})
const rankingAriaLabel = computed(() =>
  hasRankingInfo.value && props.nationalRegulationDisplay
    ? `${props.nationalRegulationDisplay.tipText} ${rankingPositionText.value}`
    : '',
)

/** 根据提示文本的实际排版行数，决定是否收紧卡片底部间距。 */
const measureRankingTipLineCount = () => {
  const element = rankingTipElement.value

  if (!hasRankingInfo.value || !element) {
    isRankingTipMultiline.value = false
    return
  }

  const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight)
  isRankingTipMultiline.value = Number.isFinite(lineHeight) && element.scrollHeight > lineHeight + 1
}

const supportedHeaderIcons = new Set<string>(['whatsapp', 'x', 'facebook', 'instagram', 'telegram', 'linkedin'])
const MAX_HEADER_SOCIAL_ICONS = 3
const headerSocialIconMap: Record<string, string> = {
  whatsapp: `${socialIconBasePath}whatsapp.png`,
  x: `${socialIconBasePath}x.png`,
  facebook: `${socialIconBasePath}facebook.png`,
  instagram: `${socialIconBasePath}instagram.png`,
  telegram: `${socialIconBasePath}telegram.png`,
  linkedin: `${socialIconBasePath}linkedin.png`,
}
const headerSocialItems = computed<HeaderSocialItem[]>(() =>
  contactSections.value
    .filter(
      (item) =>
        supportedHeaderIcons.has(item.type) &&
        !summaryItems.value.some((summaryItem) => summaryItem.contactType === item.type),
    )
    .map((item) => ({
      type: item.type,
      label: item.label,
      icon: headerSocialIconMap[item.type] || '',
    }))
    .filter((item) => Boolean(item.icon))
    .slice(0, MAX_HEADER_SOCIAL_ICONS),
)
const shouldShowMoreButton = computed(() => hasMoreFields.value)

const getLinkForIndex = (item: { urls: string[] }, index: number, value: string) => {
  const url = item.urls[index] || value

  return isUrl(url) ? url : ''
}
/** 多值字段默认展示前 3 条，用户可在当前字段内独立展开。 */
const getVisibleSectionValues = (sectionKey: string, values: string[]) =>
  expandedSectionKeys.value.includes(sectionKey) ? values : values.slice(0, COLLAPSED_VALUE_LIMIT)
const shouldShowSectionToggle = (values: string[]) => values.length > COLLAPSED_VALUE_LIMIT
const isInlineValueField = (fieldKey: ModalFieldKey) =>
  fieldKey === 'exchangeCategory' || fieldKey === 'tradingType' || fieldKey === 'businessOperations'
const getInlineFieldValue = (values: string[]) => values.filter(Boolean).join('、') || emptyText
/** 记录行内多值字段节点，用实际排版高度决定是否显示展开按钮。 */
const setInlineValueElement = (fieldKey: ModalFieldKey, element: Element | ComponentPublicInstance | null) => {
  const sectionKey = `field:${fieldKey}`
  const htmlElement = element instanceof HTMLElement ? element : null

  if (htmlElement) {
    inlineValueElements.set(sectionKey, htmlElement)
    return
  }

  inlineValueElements.delete(sectionKey)
}
/** 行内多值字段只在完整文本超过三行时提供展开能力。 */
const measureInlineFieldOverflow = () => {
  overflowingInlineFieldKeys.value = [...inlineValueElements.entries()]
    .filter(
      ([, element]) =>
        element.scrollHeight > Number.parseFloat(getComputedStyle(element).lineHeight) * INLINE_VALUE_LINE_LIMIT,
    )
    .map(([sectionKey]) => sectionKey)
}
const shouldShowFieldToggle = (field: NormalizedField) =>
  isInlineValueField(field.key)
    ? overflowingInlineFieldKeys.value.includes(`field:${field.key}`)
    : shouldShowSectionToggle(field.values)
const getSectionToggleLabel = (sectionKey: string) =>
  expandedSectionKeys.value.includes(sectionKey) ? t('83802') : t('84570')
const toggleSection = (sectionKey: string) => {
  expandedSectionKeys.value = expandedSectionKeys.value.includes(sectionKey)
    ? expandedSectionKeys.value.filter((key) => key !== sectionKey)
    : [...expandedSectionKeys.value, sectionKey]
}
const { lock: lockPageScroll, unlock: unlockPageScroll } = useStickyDialogScrollLock()
const openMoreDialog = () => {
  matomo.trackEvent('broker_detail_left', {
    type_click: '基础信息',
    name: props.exchangeName || '',
    code: props.exchangeId || '',
  })
  if (shouldShowMoreButton.value) {
    isMoreDialogOpen.value = true
  }
}
const closeMoreDialog = () => {
  matomo.trackEvent('broker_base_pop', {
    af_type: '关闭',
    name: props.exchangeName || '',
    code: props.exchangeId || '',
  })
  isMoreDialogOpen.value = false
  expandedSectionKeys.value = []
}
const trackWebsiteClick = (url: string) => {
  matomo.trackEvent('broker_base_pop', {
    af_type: url || '',
    name: props.exchangeName || '',
    code: props.exchangeId || '',
  })
  emit('openWebsite', url)
}
const handleWindowKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeMoreDialog()
  }
}

watch(isMoreDialogOpen, (isOpen) => {
  if (!import.meta.client) {
    return
  }

  if (isOpen) {
    window.addEventListener('keydown', handleWindowKeydown)
    lockPageScroll()
    nextTick(measureInlineFieldOverflow)
    return
  }

  window.removeEventListener('keydown', handleWindowKeydown)
  unlockPageScroll()
  overflowingInlineFieldKeys.value = []
})

watch(
  () => [hasRankingInfo.value, props.nationalRegulationDisplay?.tipText],
  () => {
    if (import.meta.client) {
      nextTick(measureRankingTipLineCount)
    }
  },
  { flush: 'post' },
)

onMounted(() => {
  window.addEventListener('resize', measureRankingTipLineCount)
  nextTick(measureRankingTipLineCount)
})

onBeforeUnmount(() => {
  if (!import.meta.client) {
    return
  }

  window.removeEventListener('keydown', handleWindowKeydown)
  window.removeEventListener('resize', measureRankingTipLineCount)
  unlockPageScroll()
})
</script>

<template>
  <section
    class="detail-card basic-info-card mt-[2px]"
    :class="{
      'basic-info-card-four-items': summaryItems.length === 4 && !hasRankingInfo,
      'basic-info-card-ranking-multiline': isRankingTipMultiline,
      'basic-info-card-ranking-single-row': hasRankingInfo && summaryItems.length === 2,
    }"
  >
    <div class="basic-info-header">
      <h2 class="card-title">{{ cardTitle }}</h2>
      <button
        v-show="shouldShowMoreButton"
        type="button"
        class="basic-info-more-button"
        :aria-label="moreButtonLabel"
        @click="openMoreDialog"
      >
        <span
          v-for="item in headerSocialItems"
          :key="item.type"
          class="social-badge"
          :class="`social-badge-${item.type}`"
          aria-hidden="true"
        >
          <CommonImage :src="item.icon" :alt="item.label" loading="eager" class="social-badge-img" />
        </span>
        <span class="feedback-arrow" aria-hidden="true" :class="{ 'feedback-arrow-rtl': isRtl }">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10.9766 10.0001L6.85156 5.87511L8.0299 4.69678L13.3332 10.0001L8.0299 15.3034L6.85156 14.1251L10.9766 10.0001Z"
              fill="#7D818A"
            />
          </svg>
        </span>
      </button>
    </div>

    <div class="basic-info-summary" :class="{ 'basic-info-summary-with-ranking': hasRankingInfo }">
      <div
        v-for="(column, columnIndex) in summaryColumns"
        :key="`summary-column-${columnIndex}`"
        class="basic-info-summary-column"
        :class="{ 'basic-info-summary-column-with-ranking': hasRankingInfo }"
      >
        <div v-for="item in column" :key="item.key" class="basic-info-summary-item">
          <span class="basic-info-summary-label">{{ item.label }}</span>
          <span class="basic-info-summary-value" :title="item.value">
            <CommonImage v-if="item.img" :src="item.img" loading="eager" class="basic-info-flag" alt="" />
            <span
              class="basic-info-summary-value-text"
              :class="{
                'basic-info-summary-value-text-ltr': item.key === 'website',
                'basic-info-summary-value-text-rtl': item.key === 'website' && isRtl,
              }"
              :dir="item.key === 'website' ? 'ltr' : undefined"
            >
              {{ item.value || emptyText }}
            </span>
          </span>
        </div>
      </div>
    </div>

    <NuxtLink
      v-if="hasRankingInfo"
      :to="rankingHref"
      target="_blank"
      rel="noopener noreferrer"
      class="basic-info-ranking-link flex min-w-0 cursor-pointer items-center justify-between gap-[8px] rounded-[6px] bg-[#FFF6EC] px-[10px] py-[8px] font-['PingFang_SC'] text-[12px] leading-[17px] font-[400] text-[#AF5400] transition-[filter] hover:brightness-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#AF5400]"
      :class="{ relative: isRtl }"
      :aria-label="rankingAriaLabel"
    >
      <span class="flex min-w-0 flex-1 items-center gap-[4px]" :class="{ 'pl-[52px]': isRtl }">
        <CommonImage
          v-if="nationalRegulationDisplay?.flag"
          :src="nationalRegulationDisplay.flag"
          loading="eager"
          class="size-[16px] shrink-0 rounded-full"
          alt=""
        />
        <span ref="rankingTipElement" class="basic-info-ranking-tip">{{ nationalRegulationDisplay?.tipText }}</span>
      </span>
      <span
        class="flex h-[17px] shrink-0 items-center gap-[4px] leading-[17px]"
        :class="{ 'absolute top-1/2 left-[10px] -translate-y-1/2 flex-row-reverse': isRtl }"
        dir="ltr"
      >
        <span>{{ rankingPositionText }}</span>
        <svg
          viewBox="0 0 16 16"
          class="size-[14px] shrink-0 self-center fill-current"
          :class="{ 'rotate-180': isRtl }"
          aria-hidden="true"
        >
          <path d="M10.6667 8L5.33333 2.66667L4.4 3.6L8.8 8L4.4 12.4L5.33333 13.3333L10.6667 8Z" />
        </svg>
      </span>
    </NuxtLink>

    <Teleport to="body">
      <div v-if="isMoreDialogOpen" class="basic-info-dialog-mask">
        <section
          class="basic-info-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="basic-info-dialog-title"
          @click.stop
        >
          <header class="basic-info-dialog-header">
            <h2 id="basic-info-dialog-title" class="basic-info-dialog-title">{{ dialogTitle }}</h2>
            <button type="button" class="basic-info-dialog-close" @click="closeMoreDialog">
              <span class="basic-info-dialog-close-icon" aria-hidden="true"></span>
            </button>
          </header>

          <div class="basic-info-dialog-body">
            <div class="basic-info-dialog-content">
              <div class="basic-info-dialog-grid">
                <div
                  v-for="(row, rowIndex) in topFieldRows"
                  :key="`top-row-${rowIndex}`"
                  class="basic-info-dialog-grid-row"
                >
                  <div v-for="field in row" :key="field.renderKey || field.key" class="basic-info-dialog-grid-item">
                    <p class="basic-info-dialog-label">{{ field.label }}</p>
                    <div class="basic-info-dialog-value-row">
                      <div
                        class="basic-info-dialog-value-list"
                        :class="{ 'basic-info-dialog-value-list-flag': field.key === 'registeredArea' }"
                      >
                        <template v-if="field.key === 'registeredArea'">
                          <span class="basic-info-dialog-flag-value">
                            <CommonImage
                              v-if="field.img"
                              :src="field.img"
                              loading="eager"
                              class="basic-info-dialog-flag"
                              alt=""
                            />
                            <span class="basic-info-dialog-value">{{ field.values[0] || emptyText }}</span>
                          </span>
                        </template>
                        <p
                          v-else-if="isInlineValueField(field.key)"
                          :ref="(element) => setInlineValueElement(field.key, element)"
                          class="basic-info-dialog-value basic-info-dialog-value-inline"
                          :class="{
                            'basic-info-dialog-value-inline-collapsed': !expandedSectionKeys.includes(
                              `field:${field.key}`,
                            ),
                          }"
                        >
                          {{ getInlineFieldValue(field.values) }}
                        </p>
                        <template v-else>
                          <p
                            v-for="(value, valueIndex) in getVisibleSectionValues(`field:${field.key}`, field.values)"
                            :key="`${field.key}-${valueIndex}`"
                            class="basic-info-dialog-value"
                            :class="{
                              'basic-info-dialog-value-status': field.key === 'regulationStatus' && value !== emptyText,
                            }"
                            :style="
                              field.key === 'regulationStatus' && field.color ? { color: field.color } : undefined
                            "
                          >
                            {{ value || emptyText }}
                          </p>
                        </template>
                      </div>
                      <button
                        v-if="shouldShowFieldToggle(field)"
                        type="button"
                        class="basic-info-dialog-toggle cursor-pointer hover:text-[#292a2e]"
                        :aria-label="getSectionToggleLabel(`field:${field.key}`)"
                        :aria-expanded="expandedSectionKeys.includes(`field:${field.key}`)"
                        @click="toggleSection(`field:${field.key}`)"
                      >
                        <span>{{ field.values.length }}</span>
                        <svg
                          viewBox="0 0 16 16"
                          class="basic-info-dialog-toggle-icon"
                          :class="{
                            'basic-info-dialog-toggle-icon-collapsed': !expandedSectionKeys.includes(
                              `field:${field.key}`,
                            ),
                          }"
                          aria-hidden="true"
                        >
                          <path
                            d="M8 7.21867L4.7 10.5187L3.75733 9.576L8 5.33333L12.2427 9.576L11.3 10.5187L8 7.21867Z"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="basic-info-dialog-sections">
                <div v-for="field in dialogBottomFields" :key="field.key" class="basic-info-dialog-section">
                  <p class="basic-info-dialog-label">{{ field.label }}</p>
                  <div class="basic-info-dialog-value-row">
                    <div class="basic-info-dialog-value-list">
                      <template
                        v-for="(value, valueIndex) in getVisibleSectionValues(`field:${field.key}`, field.values)"
                        :key="`${field.key}-${valueIndex}`"
                      >
                        <button
                          v-if="getLinkForIndex(field, valueIndex, value)"
                          type="button"
                          class="basic-info-dialog-link basic-info-dialog-link-button cursor-pointer hover:text-[#0d3f63]"
                          :class="{ 'basic-info-dialog-link-rtl': isRtl }"
                          dir="ltr"
                          @click="trackWebsiteClick(getLinkForIndex(field, valueIndex, value))"
                        >
                          {{ value }}
                        </button>
                        <p v-else class="basic-info-dialog-value">{{ value || emptyText }}</p>
                      </template>
                    </div>
                    <button
                      v-if="shouldShowSectionToggle(field.values)"
                      type="button"
                      class="basic-info-dialog-toggle cursor-pointer hover:text-[#292a2e]"
                      :aria-label="getSectionToggleLabel(`field:${field.key}`)"
                      :aria-expanded="expandedSectionKeys.includes(`field:${field.key}`)"
                      @click="toggleSection(`field:${field.key}`)"
                    >
                      <span>{{ field.values.length }}</span>
                      <svg
                        viewBox="0 0 16 16"
                        class="basic-info-dialog-toggle-icon"
                        :class="{
                          'basic-info-dialog-toggle-icon-collapsed': !expandedSectionKeys.includes(
                            `field:${field.key}`,
                          ),
                        }"
                        aria-hidden="true"
                      >
                        <path
                          d="M8 7.21867L4.7 10.5187L3.75733 9.576L8 5.33333L12.2427 9.576L11.3 10.5187L8 7.21867Z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div v-for="contact in contactSections" :key="contact.type" class="basic-info-dialog-section">
                  <p class="basic-info-dialog-label">{{ contact.label }}</p>
                  <div class="basic-info-dialog-value-row">
                    <div class="basic-info-dialog-value-list">
                      <template
                        v-for="(value, valueIndex) in getVisibleSectionValues(
                          `contact:${contact.type}`,
                          contact.values,
                        )"
                        :key="`${contact.type}-${valueIndex}`"
                      >
                        <a
                          v-if="getLinkForIndex(contact, valueIndex, value)"
                          :href="getLinkForIndex(contact, valueIndex, value)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="basic-info-dialog-link"
                          :class="{ 'basic-info-dialog-link-rtl': isRtl }"
                          dir="ltr"
                        >
                          {{ value }}
                        </a>
                        <p v-else class="basic-info-dialog-value">{{ value }}</p>
                      </template>
                    </div>
                    <button
                      v-if="shouldShowSectionToggle(contact.values)"
                      type="button"
                      class="basic-info-dialog-toggle cursor-pointer hover:text-[#292a2e]"
                      :aria-label="getSectionToggleLabel(`contact:${contact.type}`)"
                      :aria-expanded="expandedSectionKeys.includes(`contact:${contact.type}`)"
                      @click="toggleSection(`contact:${contact.type}`)"
                    >
                      <span>{{ contact.values.length }}</span>
                      <svg
                        viewBox="0 0 16 16"
                        class="basic-info-dialog-toggle-icon"
                        :class="{
                          'basic-info-dialog-toggle-icon-collapsed': !expandedSectionKeys.includes(
                            `contact:${contact.type}`,
                          ),
                        }"
                        aria-hidden="true"
                      >
                        <path
                          d="M8 7.21867L4.7 10.5187L3.75733 9.576L8 5.33333L12.2427 9.576L11.3 10.5187L8 7.21867Z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<style scoped lang="less">
.detail-card {
  box-sizing: border-box;
  width: 100%;
  height: 212px;
  min-height: 212px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 12px;
  background: #fff;
}

.basic-info-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
}

.basic-info-card-four-items {
  height: 220px;
  min-height: 220px;
  gap: 28px;
}

.basic-info-card-ranking-multiline {
  gap: 12px;
  padding-bottom: 20px;
}

.basic-info-card-ranking-multiline .basic-info-summary,
.basic-info-card-ranking-multiline .basic-info-summary-column {
  height: 72px;
}

.basic-info-card-ranking-single-row .basic-info-summary-column {
  justify-content: center;
}

.basic-info-ranking-tip {
  display: -webkit-box;
  min-width: 0;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.basic-info-header {
  display: flex;
  width: 100%;
  height: 26px;
  min-height: 26px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.card-title {
  min-width: 0;
  flex: 1 0 0;
  margin: 0;
  color: #3d3d3d;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
}

.basic-info-more-button {
  display: inline-flex;
  min-width: 0;
  min-height: 24px;
  align-items: center;
  justify-content: flex-end;
  gap: 0;
  margin: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.social-badge {
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid #fff;
  border-radius: 50%;
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.social-badge + .social-badge {
  margin-inline-start: -4px;
}

.social-badge:nth-child(1) {
  z-index: 1;
}

.social-badge:nth-child(2) {
  z-index: 2;
}

.social-badge:nth-child(3) {
  z-index: 3;
}

.social-badge-img {
  display: block;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
}

.basic-info-more-arrow {
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  margin-inline-start: 4px;
}

.basic-info-more-arrow-svg {
  width: 16px;
  height: 16px;
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
  fill: #7d818a;
}

.basic-info-more-button:hover .basic-info-more-arrow-svg {
  fill: #292a2e;
}

.feedback-arrow {
  width: 20px;
  height: 20px;
  color: #7d818a;
  &:hover {
    color: #292a2e;
  }
}

.feedback-arrow-rtl {
  transform: rotate(180deg);
}

.feedback-arrow :deep(path) {
  fill: currentColor;
}

.basic-info-more-button:focus-visible,
.basic-info-dialog-close:focus-visible,
.basic-info-dialog-link:focus-visible {
  outline: 2px solid rgba(36, 104, 222, 0.35);
  outline-offset: 2px;
}

.basic-info-summary {
  display: grid;
  width: 100%;
  height: 138px;
  grid-template-columns: minmax(0, 253px) minmax(0, 1fr);
  gap: 12px;
}

.basic-info-summary-column {
  display: flex;
  min-width: 0;
  height: 138px;
  flex-direction: column;
  gap: 6px;
}

.basic-info-summary-with-ranking,
.basic-info-summary-column-with-ranking {
  height: 88px;
}

.basic-info-summary-column-with-ranking {
  gap: 4px;
}

.basic-info-card-four-items .basic-info-summary {
  height: 126px;
}

.basic-info-card-four-items .basic-info-summary-column {
  height: 126px;
  justify-content: space-between;
  gap: 0;
}

.basic-info-summary-item {
  display: flex;
  width: 100%;
  height: 42px;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
}

.basic-info-card-four-items .basic-info-summary-item {
  height: auto;
  gap: 10px;
}

.basic-info-summary-label {
  width: 100%;
  height: 20px;
  min-width: 0;
  overflow: hidden;
  color: #7d818a;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.basic-info-summary-value {
  display: inline-flex;
  width: 100%;
  height: 22px;
  min-width: 0;
  align-items: center;
  color: #292a2e;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

.basic-info-summary-value-status {
  color: #1f845a;
}

.basic-info-summary-value-text {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.basic-info-summary-value-text-ltr {
  direction: ltr;
  text-align: start;
  unicode-bidi: isolate;
}

.basic-info-summary-value-text-rtl {
  text-align: right;
}

.basic-info-flag {
  width: 18px;
  height: 12px;
  flex: 0 0 18px;
  border-radius: 1px;
  margin-inline-end: 4px;
  object-fit: cover;
}

.basic-info-dialog-mask {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(5, 21, 36, 0.36);
  padding: 24px;
}

.basic-info-dialog {
  position: relative;
  z-index: 1;
  width: min(720px, calc(100vw - 48px));
  max-height: min(600px, calc(100vh - 48px));
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(5, 21, 36, 0.18);
}

.basic-info-dialog-header {
  position: relative;
  display: flex;
  min-height: 80px;
  align-items: flex-start;
  gap: 8px;
  padding: 32px 32px 0;
}

.basic-info-dialog-header::after {
  position: absolute;
  right: 32px;
  bottom: 0;
  left: 32px;
  height: 0.5px;
  background: rgba(5, 21, 36, 0.06);
  content: '';
}

.basic-info-dialog-title {
  flex: 1 0 0;
  min-width: 0;
  margin: 0;
  color: #292a2e;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
}

.basic-info-dialog-close {
  position: relative;
  z-index: 1;
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #7d818a;
  cursor: pointer;
  transition: color 0.2s ease;
}

.basic-info-dialog-close-icon {
  width: 32px;
  height: 32px;
  background: url('https://resources1.interface003.com/web30/2026/07/18/c4bf741c-fc52-443e-9be5-8a3f9d8c50bb.svg')
    center / contain no-repeat;
}

.basic-info-dialog-body {
  max-height: calc(min(600px, calc(100vh - 48px)) - 80px);
  overflow-y: auto;
}

.basic-info-dialog-body::-webkit-scrollbar {
  display: block;
  width: 5px;
}

.basic-info-dialog-body::-webkit-scrollbar-track {
  border-radius: 8px;
  background: transparent;
}

.basic-info-dialog-body::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
}

.basic-info-dialog-content {
  box-sizing: border-box;
  width: 100%;
  padding: 20px 28px 32px 32px;
}

.basic-info-dialog-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.basic-info-dialog-grid-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.basic-info-dialog-grid-item,
.basic-info-dialog-section {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
  border-bottom: 0.5px solid rgba(5, 21, 36, 0.06);
  padding-bottom: 12px;
}

.basic-info-dialog-label {
  margin: 0;
  color: #7d818a;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
}

.basic-info-dialog-value-list {
  display: flex;
  min-width: 0;
  flex: 1 0 0;
  flex-direction: column;
  gap: 8px;
}

.basic-info-dialog-value-row {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 8px;
}

.basic-info-dialog-value-list-flag {
  gap: 0;
}

.basic-info-dialog-flag-value {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.basic-info-dialog-flag {
  width: 18px;
  height: 12px;
  flex: 0 0 18px;
  border-radius: 1px;
  object-fit: cover;
}

.basic-info-dialog-value,
.basic-info-dialog-link {
  margin: 0;
  color: #292a2e;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  word-break: break-word;
}

.basic-info-dialog-value-status {
  color: #1f845a;
}

.basic-info-dialog-value-inline-collapsed {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.basic-info-dialog-link {
  direction: ltr;
  text-align: start;
  text-decoration: none;
  transition: color 0.2s ease;
  unicode-bidi: isolate;
}

.basic-info-dialog-link-button {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
}

.basic-info-dialog-link-rtl {
  text-align: right;
}

.basic-info-dialog-link:hover {
  color: #0d3f63;
}

.basic-info-dialog-toggle {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  margin: 0;
  border: 0;
  background: transparent;
  color: #505258;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  padding: 0;
  transition: color 0.2s ease;
  white-space: nowrap;
}

.basic-info-dialog-toggle-icon {
  width: 16px;
  height: 16px;
  fill: #7d818a;
  transition: transform 0.2s ease;
}

.basic-info-dialog-toggle-icon-collapsed {
  transform: rotate(180deg);
}

.basic-info-dialog-sections {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
}

@media screen and (max-width: 800px) {
  .basic-info-dialog-mask {
    padding: 16px;
  }

  .basic-info-dialog {
    width: calc(100vw - 32px);
  }

  .basic-info-dialog-header {
    padding: 24px 20px 0;
  }

  .basic-info-dialog-header::after {
    right: 20px;
    left: 20px;
  }

  .basic-info-dialog-body {
    max-height: calc(min(600px, calc(100vh - 32px)) - 80px);
  }

  .basic-info-dialog-content {
    padding: 20px 16px 24px 20px;
  }
}

@media screen and (max-width: 680px) {
  .basic-info-dialog-grid-row {
    grid-template-columns: 1fr;
  }
}
</style>
